import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Node, mergeAttributes } from '@tiptap/core';
import { NodeViewWrapper, ReactNodeViewRenderer } from '@tiptap/react';
import { runPython, deliverCanvasEvent } from '../components/PyodideRunner';
import { OgeRobotLangError, prepareRobotSource } from '../robot/ogeRobotLang';
import KonspektPythonCodeMirror from './KonspektPythonCodeMirror';
import CodeCanvas, { EVENT_CONTROL, EVENT_IN, scopedEvent } from '../components/workspace/CodeCanvas';

export const KONSPEKT_DEFAULT_PYTHON = 'print("Привет")';
const KONSPEKT_RUN_START = 'diobuch-konspekt-py-run';

function codeNeedsCanvas(src) {
  return /\b(import\s+turtle|from\s+turtle\s+import|diobuch_turtle|import\s+robot|from\s+robot\s+import|diobuch_robot|import\s+game|from\s+game\s+import|diobuch_game|import\s+canvas|from\s+canvas\s+import|diobuch_canvas)\b/.test(
    String(src || ''),
  );
}

function parseCanvasAttr(raw) {
  if (!raw) return {};
  if (typeof raw === 'object') return raw;
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function isOgeRobotCanvas(cfg) {
  const lang = String(cfg?.input_lang || cfg?.lang || '').toLowerCase();
  return cfg?.mode === 'robot' || lang === 'oge' || lang === 'oge_robot' || lang === 'kumir';
}

function canvasModeFromCode(src) {
  const s = String(src || '');
  if (/\b(import\s+robot|from\s+robot\s+import|diobuch_robot)\b/.test(s)) return 'robot';
  if (/\b(import\s+game|from\s+game\s+import|diobuch_game|import\s+canvas|from\s+canvas\s+import|diobuch_canvas)\b/.test(s)) {
    return 'game';
  }
  return 'turtle';
}

function routeCanvasCmd(busId, cmd) {
  if (!cmd || typeof cmd !== 'object') return;
  if (cmd.type === '_control' || cmd.control) {
    const control = cmd.control || cmd.type;
    const detail = {
      type: control === 'start_tick' || control === 'stop_tick' || control === 'after' || control === 'cancel_after'
        ? control
        : String(control),
      ms: cmd.ms,
      id: cmd.id,
    };
    window.dispatchEvent(new CustomEvent(scopedEvent(EVENT_CONTROL, busId), { detail }));
    return;
  }
  window.dispatchEvent(new CustomEvent(scopedEvent(EVENT_IN, busId), { detail: cmd }));
}

async function copyToClipboard(text, onDone) {
  try {
    await navigator.clipboard.writeText(text);
    onDone?.(true);
    return true;
  } catch {
    try {
      window.prompt('Скопируйте код вручную (Ctrl+C):', text);
    } catch {
      /* ignore */
    }
    onDone?.(false);
    return false;
  }
}

function KonspektPyRunNodeView({ node, updateAttributes, editor }) {
  const isAuthor = editor.isEditable;
  const reactId = useId();
  const busId = `konspekt-${reactId.replace(/:/g, '')}`;
  const [readerCode, setReaderCode] = useState(() => node.attrs.code || '');
  const [readerStdin, setReaderStdin] = useState(() => node.attrs.stdin || '');
  const [output, setOutput] = useState('');
  const [running, setRunning] = useState(false);
  const [copied, setCopied] = useState(false);
  const abortRef = useRef(null);
  const busIdRef = useRef(busId);
  busIdRef.current = busId;

  const code = isAuthor ? node.attrs.code || '' : readerCode;
  const stdin = isAuthor ? node.attrs.stdin || '' : readerStdin;
  const canvasCfg = parseCanvasAttr(node.attrs.canvas);
  const robotFromCanvas = isOgeRobotCanvas(canvasCfg);
  const showCanvas = codeNeedsCanvas(code) || robotFromCanvas;
  const canvasMode = robotFromCanvas ? 'robot' : canvasModeFromCode(code);
  // Совпадает с game.WIDTH/HEIGHT в worker, иначе объекты «ниже» холста не видны.
  const canvasHeight = canvasMode === 'game' ? 480 : canvasMode === 'robot' ? 320 : 220;
  const canvasWidth = canvasMode === 'game' ? 480 : undefined;

  const stopLocal = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    deliverCanvasEvent({ type: 'stop' });
    window.dispatchEvent(new CustomEvent(scopedEvent(EVENT_CONTROL, busIdRef.current), {
      detail: { type: 'stop_tick' },
    }));
    window.dispatchEvent(new CustomEvent(scopedEvent(EVENT_IN, busIdRef.current), {
      detail: { type: 'session', active: false },
    }));
    setRunning(false);
  }, []);

  // Другой блок на странице запустился — остановить этот (один worker).
  useEffect(() => {
    function onOtherRun(ev) {
      if (ev?.detail?.busId === busIdRef.current) return;
      if (abortRef.current) stopLocal();
    }
    window.addEventListener(KONSPEKT_RUN_START, onOtherRun);
    return () => window.removeEventListener(KONSPEKT_RUN_START, onOtherRun);
  }, [stopLocal]);

  const onCodeChange = useCallback(
    (next) => {
      if (isAuthor) updateAttributes({ code: next });
      else setReaderCode(next);
    },
    [isAuthor, updateAttributes],
  );

  const onStdinChange = useCallback(
    (e) => {
      const v = e.target.value;
      if (isAuthor) updateAttributes({ stdin: v });
      else setReaderStdin(v);
    },
    [isAuthor, updateAttributes],
  );

  useEffect(() => {
    return () => {
      if (abortRef.current) abortRef.current.abort();
    };
  }, []);

  const handleRun = async () => {
    if (running) {
      stopLocal();
      return;
    }
    if (abortRef.current) abortRef.current.abort();
    let source = code;
    if (canvasMode === 'robot') {
      try {
        source = prepareRobotSource(code, canvasCfg);
      } catch (err) {
        const text = err instanceof OgeRobotLangError
          ? err.message
          : String(err?.message || err);
        setOutput(text);
        return;
      }
    }
    window.dispatchEvent(new CustomEvent(KONSPEKT_RUN_START, { detail: { busId } }));
    const ac = new AbortController();
    abortRef.current = ac;
    setRunning(true);
    setOutput('');
    let acc = '';
    try {
      const result = await runPython(source, stdin, {}, {
        signal: ac.signal,
        canvasConfig: {
          ...canvasCfg,
          mode: canvasMode,
          ...(canvasMode === 'game' ? { width: 480, height: canvasHeight } : {}),
        },
        onCanvas: (cmd) => routeCanvasCmd(busId, cmd),
        onStdoutChunk: (chunk) => {
          acc += chunk;
          setOutput(acc);
        },
      });
      if (ac.signal.aborted) return;
      if (result.success) {
        const text = (result.output ?? acc).trimEnd();
        setOutput(text.length ? text : '(нет вывода)');
      } else {
        const errText = (result.error || 'Ошибка').trim();
        const out = (result.output || acc || '').trimEnd();
        setOutput(out ? `${out}\n\n${errText}` : errText);
      }
    } catch (err) {
      if (err?.name === 'AbortError') return;
      setOutput(String(err.message || err));
    } finally {
      if (!ac.signal.aborted) setRunning(false);
      abortRef.current = null;
    }
  };

  const handleCopy = () => {
    copyToClipboard(code, (ok) => {
      if (ok) {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    });
  };

  return (
    <NodeViewWrapper className="konspekt-py-run-wrap" data-drag-handle="">
      <div className="konspekt-py-run-head">
        <span className="konspekt-py-run-title">
          {canvasMode === 'robot' ? 'Робот' : 'Пример с запуском (Python)'}
        </span>
        <div className="konspekt-py-run-actions">
          <button type="button" className="konspekt-py-btn" onClick={handleCopy} disabled={running}>
            {copied ? 'Скопировано' : 'Копировать код'}
          </button>
          <button type="button" className="konspekt-py-btn konspekt-py-btn-primary" onClick={handleRun}>
            {running ? 'Стоп' : 'Запустить'}
          </button>
        </div>
      </div>
      <KonspektPythonCodeMirror value={code} onChange={onCodeChange} readOnly={false} height="min(36vh, 200px)" />
      {showCanvas ? (
        <div
          className="konspekt-py-canvas"
          style={canvasWidth ? { maxWidth: canvasWidth, marginInline: 'auto' } : undefined}
        >
          <CodeCanvas
            mode={canvasMode}
            height={canvasHeight}
            busId={busId}
            config={canvasCfg}
            onCanvasEvent={(ev) => deliverCanvasEvent(ev)}
          />
        </div>
      ) : null}
      {canvasMode === 'robot' ? null : (
      <label className="konspekt-py-stdin-label">
        Ввод (stdin)
        <textarea
          className="konspekt-py-stdin"
          value={stdin}
          onChange={onStdinChange}
          rows={3}
          placeholder="Текст для input() — по одной строке на вызов"
          spellCheck={false}
        />
      </label>
      )}
      <div className="konspekt-py-out-label">Вывод</div>
      <pre className="konspekt-py-output" aria-live="polite">
        {output || '—'}
      </pre>
    </NodeViewWrapper>
  );
}

function KonspektCodeStaticNodeView({ node, updateAttributes, editor }) {
  const isAuthor = editor.isEditable;
  const [copied, setCopied] = useState(false);
  const code = node.attrs.code || '';

  const handleCopy = () => {
    copyToClipboard(code, (ok) => {
      if (ok) {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    });
  };

  return (
    <NodeViewWrapper className="konspekt-code-static-wrap" data-drag-handle="">
      <div className="konspekt-code-static-head">
        <span className="konspekt-code-static-title">Пример кода</span>
        <button type="button" className="konspekt-py-btn" onClick={handleCopy}>
          {copied ? 'Скопировано' : 'Копировать код'}
        </button>
      </div>
      <KonspektPythonCodeMirror
        value={code}
        onChange={isAuthor ? (c) => updateAttributes({ code: c }) : undefined}
        readOnly={!isAuthor}
        height="min(32vh, 180px)"
      />
    </NodeViewWrapper>
  );
}

export const KonspektPyRun = Node.create({
  name: 'konspektPyRun',
  group: 'block',
  atom: true,
  draggable: true,
  addAttributes() {
    return {
      code: { default: KONSPEKT_DEFAULT_PYTHON, rendered: false },
      stdin: { default: '', rendered: false },
      canvas: { default: '', rendered: false },
    };
  },
  parseHTML() {
    return [
      {
        tag: 'div[data-type="konspekt-py-run"]',
        getAttrs: (el) => ({
          code: safeDecode(el.getAttribute('data-code')),
          stdin: safeDecode(el.getAttribute('data-stdin')),
          canvas: safeDecode(el.getAttribute('data-canvas')),
        }),
      },
    ];
  },
  renderHTML({ HTMLAttributes, node }) {
    return [
      'div',
      mergeAttributes(HTMLAttributes, {
        'data-type': 'konspekt-py-run',
        'data-code': encodeURIComponent(node.attrs.code || ''),
        'data-stdin': encodeURIComponent(node.attrs.stdin || ''),
        'data-canvas': encodeURIComponent(node.attrs.canvas || ''),
      }),
    ];
  },
  addNodeView() {
    return ReactNodeViewRenderer(KonspektPyRunNodeView);
  },
});

export const KonspektCodeStatic = Node.create({
  name: 'konspektCodeStatic',
  group: 'block',
  atom: true,
  draggable: true,
  addAttributes() {
    return {
      code: { default: KONSPEKT_DEFAULT_PYTHON, rendered: false },
    };
  },
  parseHTML() {
    return [
      {
        tag: 'div[data-type="konspekt-code-static"]',
        getAttrs: (el) => ({
          code: safeDecode(el.getAttribute('data-code')),
        }),
      },
    ];
  },
  renderHTML({ HTMLAttributes, node }) {
    return [
      'div',
      mergeAttributes(HTMLAttributes, {
        'data-type': 'konspekt-code-static',
        'data-code': encodeURIComponent(node.attrs.code || ''),
      }),
    ];
  },
  addNodeView() {
    return ReactNodeViewRenderer(KonspektCodeStaticNodeView);
  },
});

function safeDecode(raw) {
  if (raw == null || raw === '') return '';
  try {
    return decodeURIComponent(raw);
  } catch {
    return raw;
  }
}
